import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { clientAPI, walletAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  ShoppingBagIcon,
  PhoneIcon,
  MapPinIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  CreditCardIcon,
  WalletIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  { value: 'processing', label: 'Processing', color: 'bg-purple-100 text-purple-700' },
  { value: 'ready', label: 'Ready', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'shipped', label: 'Shipped', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' }
];

const ITEMS_PER_PAGE = 10;

const ClientOrders = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState({
    status: '',
    paymentStatus: '',
    search: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Payment state
  const [showPayment, setShowPayment] = useState(false);
  const [payingOrder, setPayingOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [canPayWithWallet, setCanPayWithWallet] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Delivery confirmation state
  const [confirmingDelivery, setConfirmingDelivery] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await clientAPI.listOrders({});
      setOrders(res.data.orders || []);
    } catch (error) {
      console.error('Load orders error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // Filter by status
    if (filter.status) {
      result = result.filter(o => o.status === filter.status);
    }

    // Filter by payment status
    if (filter.paymentStatus) {
      result = result.filter(o => o.paymentStatus === filter.paymentStatus);
    }

    // Filter by search (order number, farm name)
    if (filter.search) {
      const search = filter.search.toLowerCase();
      result = result.filter(o =>
        o.orderNumber?.toLowerCase().includes(search) ||
        o.farm?.name?.toLowerCase().includes(search)
      );
    }

    // Filter by date range
    if (filter.dateFrom) {
      const fromDate = new Date(filter.dateFrom);
      result = result.filter(o => new Date(o.createdAt) >= fromDate);
    }
    if (filter.dateTo) {
      const toDate = new Date(filter.dateTo);
      toDate.setHours(23, 59, 59);
      result = result.filter(o => new Date(o.createdAt) <= toDate);
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (filter.sortBy) {
        case 'totalAmount':
          aVal = Number(a.totalAmount || 0);
          bVal = Number(b.totalAmount || 0);
          break;
        case 'farm':
          aVal = a.farm?.name?.toLowerCase() || '';
          bVal = b.farm?.name?.toLowerCase() || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
      }
      if (filter.sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [orders, filter]);

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <span className={`inline-flex px-2 py-1 text-xs rounded-full ${opt.color}`}>{opt.label}</span>;
  };

  const clearFilters = () => {
    setFilter({
      status: '',
      paymentStatus: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  // Payment not allowed reason state
  const [paymentBlockedReason, setPaymentBlockedReason] = useState(null);
  
  // Open payment modal
  const openPaymentModal = async (order) => {
    setPayingOrder(order);
    setPaymentMethod('mpesa');
    setMpesaPhone('');
    setPaymentSent(false);
    setPaymentBlockedReason(null);
    setShowPayment(true);
    
    // Always fetch wallet balance first (most reliable)
    let balance = 0;
    try {
      const walletRes = await walletAPI.getWallet();
      balance = Number(walletRes.data?.wallet?.availableBalance || 0);
      setWalletBalance(balance);
    } catch (err) {
      console.error('Failed to get wallet:', err);
      setWalletBalance(0);
    }
    
    // Then check payment eligibility for this order
    try {
      const checkRes = await clientAPI.checkWalletPayment(order.id);
      const canPay = checkRes.data?.canPay ?? false;
      setCanPayWithWallet(canPay);
      
      // If payment is blocked due to order status, store the reason
      const reason = checkRes.data?.reason;
      if (!canPay && reason && 
          (reason.includes('seller') || reason.includes('cancelled') || reason.includes('Waiting'))) {
        setPaymentBlockedReason(reason);
      }
    } catch (err) {
      console.error('Failed to check payment eligibility:', err);
      // If error, determine canPay based on balance vs order amount
      const orderAmount = Number(order.totalAmount) || 0;
      setCanPayWithWallet(balance >= orderAmount);
      
      // Check if error response has a reason
      const errorReason = err.response?.data?.reason || err.response?.data?.message;
      if (errorReason && (errorReason.includes('seller') || errorReason.includes('cancelled') || errorReason.includes('Waiting'))) {
        setPaymentBlockedReason(errorReason);
      }
    }
  };

  // Process M-Pesa payment
  const handleMpesaPayment = async () => {
    if (!mpesaPhone.trim()) {
      toast.error('Please enter your M-Pesa phone number');
      return;
    }
    
    setProcessing(true);
    try {
      await clientAPI.initiatePayment(payingOrder.id, mpesaPhone);
      setPaymentSent(true);
      toast.success('Payment request sent! Check your phone for M-Pesa prompt.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  // Process wallet payment
  const handleWalletPayment = async () => {
    if (!canPayWithWallet) {
      toast.error('Insufficient wallet balance');
      return;
    }
    
    setProcessing(true);
    try {
      await clientAPI.payWithWallet(payingOrder.id);
      toast.success('Payment successful! Funds held in escrow until delivery.');
      setShowPayment(false);
      setPayingOrder(null);
      loadOrders(); // Refresh orders
    } catch (error) {
      toast.error(error.response?.data?.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  // Check payment status (for M-Pesa)
  const checkPaymentStatus = async () => {
    setCheckingPayment(true);
    try {
      const res = await clientAPI.checkPaymentStatus(payingOrder.id);
      if (res.data.paid) {
        toast.success('Payment confirmed!');
        setShowPayment(false);
        setPayingOrder(null);
        loadOrders();
      } else {
        toast.info('Payment not yet received. Please complete on your phone.');
      }
    } catch (error) {
      toast.error('Failed to check payment status');
    } finally {
      setCheckingPayment(false);
    }
  };

  // Confirm delivery - releases escrow to seller
  const handleConfirmDelivery = async (order) => {
    if (!window.confirm(`Confirm you have received order #${order.orderNumber}?\n\nThis will release payment to the seller.`)) {
      return;
    }
    
    setConfirmingDelivery(order.id);
    try {
      await clientAPI.confirmDelivery(order.id);
      toast.success('Delivery confirmed! Thank you for your purchase.');
      loadOrders();
      if (selectedOrder?.id === order.id) {
        setSelectedOrder(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to confirm delivery');
    } finally {
      setConfirmingDelivery(null);
    }
  };

  const handleSort = (column) => {
    setFilter(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIndicator = ({ column }) => {
    if (filter.sortBy !== column) return null;
    return <span className="ml-1">{filter.sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <ShoppingBagIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">My Orders</h1>
              <p className="text-amber-100">Track and view your order history</p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="relative mt-6 grid grid-cols-4 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.length}</p>
            <p className="text-amber-200 text-xs">Total</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.filter(o => o.status === 'pending').length}</p>
            <p className="text-amber-200 text-xs">Pending</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.filter(o => ['confirmed', 'processing', 'ready', 'shipped'].includes(o.status)).length}</p>
            <p className="text-amber-200 text-xs">In Progress</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.filter(o => o.status === 'delivered').length}</p>
            <p className="text-amber-200 text-xs">Delivered</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-700">Filters</span>
          {(filter.status || filter.paymentStatus || filter.search || filter.dateFrom || filter.dateTo) && (
            <button
              onClick={clearFilters}
              className="ml-2 text-sm text-amber-600 hover:text-amber-700"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search order, farm..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
          </div>

          {/* Status Filter */}
          <select
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          >
            <option value="">All Status</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          {/* Payment Filter */}
          <select
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
            value={filter.paymentStatus}
            onChange={(e) => setFilter({ ...filter, paymentStatus: e.target.value })}
          >
            <option value="">All Payment</option>
            <option value="pending">Unpaid</option>
            <option value="paid">Paid</option>
          </select>

          {/* Date From */}
          <input
            type="date"
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
            value={filter.dateFrom}
            onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })}
          />

          {/* Date To */}
          <input
            type="date"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            value={filter.dateTo}
            onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })}
          />
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Showing {paginatedOrders.length} of {filteredOrders.length} orders
          {filteredOrders.length !== orders.length && ` (${orders.length} total)`}
        </span>
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <ShoppingBagIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
          <p className="text-gray-500 mb-6">Start exploring farms to place your first order</p>
          <Link
            to="/client/explore"
            className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Explore Farms
          </Link>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <FunnelIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No matching orders</h3>
          <p className="text-gray-500 mb-6">Try adjusting your filters</p>
          <button
            onClick={clearFilters}
            className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Mobile Card View */}
          <div className="block md:hidden divide-y divide-gray-200">
            {paginatedOrders.map((order) => (
              <div key={order.id} className="p-4">
                <div className="flex items-start justify-between mb-2" onClick={() => setSelectedOrder(order)}>
                  <div>
                    <p className="font-semibold text-gray-900">#{order.orderNumber}</p>
                    <p className="text-xs text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">KES {Number(order.totalAmount || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{order.items?.length || 0} items</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-3" onClick={() => setSelectedOrder(order)}>
                  <div>
                    <p className="text-sm text-gray-700">{order.farm?.name || 'Unknown Farm'}</p>
                    <p className="text-xs text-gray-500">{order.farm?.county || ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(order.status)}
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                      order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {order.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                </div>
                {/* Mobile Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="flex-1 py-2 text-center text-amber-600 hover:text-amber-700 text-sm font-medium border border-amber-200 rounded-lg"
                  >
                    View Details
                  </button>
                  {/* Show awaiting confirmation for pending orders */}
                  {order.paymentStatus === 'pending' && order.status === 'pending' && (
                    <span className="flex-1 py-2 text-center text-amber-600 text-sm font-medium bg-amber-50 rounded-lg border border-amber-200">
                      Awaiting Confirmation
                    </span>
                  )}
                  {/* Show Pay button only for confirmed orders that are unpaid */}
                  {order.paymentStatus === 'pending' && order.status !== 'pending' && order.status !== 'cancelled' && (
                    <button
                      onClick={() => openPaymentModal(order)}
                      className="flex-1 py-2 text-center bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                    >
                      Pay Now
                    </button>
                  )}
                  {order.status === 'shipped' && order.paymentStatus === 'paid' && (
                    <button
                      onClick={() => handleConfirmDelivery(order)}
                      disabled={confirmingDelivery === order.id}
                      className="flex-1 py-2 text-center bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {confirmingDelivery === order.id ? 'Confirming...' : 'Confirm Delivery'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('createdAt')}
                  >
                    Order <SortIndicator column="createdAt" />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('farm')}
                  >
                    Farm <SortIndicator column="farm" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('totalAmount')}
                  >
                    Total <SortIndicator column="totalAmount" />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('status')}
                  >
                    Status <SortIndicator column="status" />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">#{order.orderNumber}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm text-gray-900">{order.farm?.name || 'Unknown Farm'}</p>
                        <p className="text-xs text-gray-500">{order.farm?.county || ''}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-gray-900">{order.items?.length || 0} items</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-medium text-gray-900">
                        KES {Number(order.totalAmount || 0).toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="text-amber-600 hover:text-amber-700 text-sm font-medium"
                        >
                          View
                        </button>
                        {/* Show awaiting confirmation badge for pending orders */}
                        {order.paymentStatus === 'pending' && order.status === 'pending' && (
                          <span className="inline-flex items-center px-3 py-1 text-amber-700 text-sm bg-amber-50 rounded-lg border border-amber-200">
                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Awaiting Confirmation
                          </span>
                        )}
                        {/* Show Pay button only for confirmed orders that are unpaid */}
                        {order.paymentStatus === 'pending' && order.status !== 'pending' && order.status !== 'cancelled' && (
                          <button
                            onClick={() => openPaymentModal(order)}
                            className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                          >
                            <CreditCardIcon className="h-4 w-4 mr-1" />
                            Pay
                          </button>
                        )}
                        {order.status === 'shipped' && order.paymentStatus === 'paid' && (
                          <button
                            onClick={() => handleConfirmDelivery(order)}
                            disabled={confirmingDelivery === order.id}
                            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            {confirmingDelivery === order.id ? (
                              <>
                                <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Confirming...
                              </>
                            ) : (
                              <>
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Confirm Delivery
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {/* Page numbers - show fewer on mobile */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm rounded-lg ${
                          currentPage === pageNum
                            ? 'bg-amber-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                {/* Mobile: just show current/total */}
                <span className="sm:hidden px-3 py-2 text-sm bg-amber-600 text-white rounded-lg">
                  {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Order #{selectedOrder.orderNumber}</h3>
                  <p className="text-sm text-gray-500">
                    {new Date(selectedOrder.createdAt).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Status</span>
                {getStatusBadge(selectedOrder.status)}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-500">Payment</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  selectedOrder.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {selectedOrder.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>

              {/* Farm Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-900">{selectedOrder.farm?.name || 'Farm'}</p>
                {selectedOrder.farm?.phone && (
                  <p className="text-sm text-gray-600 flex items-center mt-1">
                    <PhoneIcon className="h-4 w-4 mr-1" />
                    {selectedOrder.farm.phone}
                  </p>
                )}
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Order Items</h4>
                <div className="space-y-2">
                  {(selectedOrder.items || []).map((item, i) => (
                    <div key={i} className="flex justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{item.productName}</p>
                        <p className="text-sm text-gray-500">
                          {item.quantity} x KES {Number(item.unitPrice).toLocaleString()}
                        </p>
                      </div>
                      <p className="font-medium text-gray-900">
                        KES {Number(item.totalPrice || item.quantity * item.unitPrice).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between font-semibold">
                    <span>Total</span>
                    <span>KES {Number(selectedOrder.totalAmount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Delivery Info */}
              {selectedOrder.deliveryAddress && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Delivery Address</h4>
                  <p className="text-sm text-gray-600 flex items-start">
                    <MapPinIcon className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    {selectedOrder.deliveryAddress}
                  </p>
                </div>
              )}

              {/* Awaiting Confirmation Message in Modal */}
              {selectedOrder.paymentStatus === 'pending' && selectedOrder.status === 'pending' && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="font-medium text-amber-800">Awaiting Seller Confirmation</p>
                        <p className="text-sm text-amber-700 mt-1">
                          The seller will review your order and confirm availability. You'll be notified when payment is enabled.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pay Button in Modal - only for confirmed orders */}
              {selectedOrder.paymentStatus === 'pending' && selectedOrder.status !== 'pending' && selectedOrder.status !== 'cancelled' && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setSelectedOrder(null);
                      openPaymentModal(selectedOrder);
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                    Pay Now - KES {Number(selectedOrder.totalAmount).toLocaleString()}
                  </button>
                </div>
              )}

              {/* Confirm Delivery Button in Modal */}
              {selectedOrder.status === 'shipped' && selectedOrder.paymentStatus === 'paid' && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <TruckIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">Order Shipped!</p>
                        <p className="text-sm text-blue-600">
                          Please confirm delivery once you receive your order. This will release payment to the seller.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleConfirmDelivery(selectedOrder)}
                    disabled={confirmingDelivery === selectedOrder.id}
                    className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {confirmingDelivery === selectedOrder.id ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Confirming...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        Confirm Delivery
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && payingOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Pay for Order</h3>
                  <p className="text-sm text-gray-500">#{payingOrder.orderNumber}</p>
                </div>
                <button
                  onClick={() => { setShowPayment(false); setPayingOrder(null); }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Amount to Pay</span>
                  <span className="text-xl font-bold text-gray-900">
                    KES {Number(payingOrder.totalAmount).toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{payingOrder.farm?.name}</p>
              </div>

              {/* Escrow Notice */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <ShieldCheckIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Escrow Protection</p>
                  <p className="text-blue-600">Your payment is held securely until the order is delivered.</p>
                </div>
              </div>

              {/* Show blocked reason if payment not allowed */}
              {paymentBlockedReason && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Payment Not Available Yet</p>
                    <p className="text-amber-700 mt-1">{paymentBlockedReason}</p>
                    <p className="text-amber-600 text-xs mt-2">You'll be notified when the seller confirms your order.</p>
                  </div>
                </div>
              )}

              {paymentBlockedReason ? (
                /* Payment Blocked - Just show close button */
                <button
                  onClick={() => { setShowPayment(false); setPayingOrder(null); setPaymentBlockedReason(null); }}
                  className="w-full py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              ) : !paymentSent ? (
                <>
                  {/* Payment Method Selection */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                    
                    {/* M-Pesa Option */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('mpesa')}
                      className={`w-full flex items-center gap-3 p-4 border-2 rounded-lg transition-colors ${
                        paymentMethod === 'mpesa'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="h-10 w-10 bg-green-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">M</span>
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-medium text-gray-900">M-Pesa</p>
                        <p className="text-sm text-gray-500">Pay via STK Push</p>
                      </div>
                      {paymentMethod === 'mpesa' && (
                        <div className="h-5 w-5 bg-green-600 rounded-full flex items-center justify-center">
                          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>

                    {/* Wallet Option */}
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('wallet')}
                      disabled={!canPayWithWallet}
                      className={`w-full flex items-center gap-3 p-4 border-2 rounded-lg transition-colors ${
                        paymentMethod === 'wallet'
                          ? 'border-amber-500 bg-amber-50'
                          : canPayWithWallet
                            ? 'border-gray-200 hover:border-gray-300'
                            : 'border-gray-200 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="h-10 w-10 bg-amber-600 rounded-full flex items-center justify-center">
                        <WalletIcon className="h-5 w-5 text-white" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-medium text-gray-900">Wallet</p>
                        <p className="text-sm text-gray-500">
                          Balance: KES {walletBalance.toLocaleString()}
                          {!canPayWithWallet && walletBalance < Number(payingOrder.totalAmount) && (
                            <span className="text-red-500 ml-1">(Insufficient)</span>
                          )}
                        </p>
                      </div>
                      {paymentMethod === 'wallet' && (
                        <div className="h-5 w-5 bg-amber-600 rounded-full flex items-center justify-center">
                          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  </div>

                  {/* M-Pesa Phone Input */}
                  {paymentMethod === 'mpesa' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        M-Pesa Phone Number
                      </label>
                      <input
                        type="tel"
                        placeholder="07XXXXXXXX"
                        value={mpesaPhone}
                        onChange={(e) => setMpesaPhone(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  )}

                  {/* Pay Button */}
                  <button
                    onClick={paymentMethod === 'mpesa' ? handleMpesaPayment : handleWalletPayment}
                    disabled={processing || (paymentMethod === 'mpesa' && !mpesaPhone.trim()) || (paymentMethod === 'wallet' && !canPayWithWallet)}
                    className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCardIcon className="h-5 w-5 mr-2" />
                        Pay KES {Number(payingOrder.totalAmount).toLocaleString()}
                      </>
                    )}
                  </button>
                </>
              ) : (
                /* Payment Sent - Waiting */
                <div className="text-center space-y-4">
                  <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Check Your Phone</h4>
                    <p className="text-sm text-gray-500">Enter your M-Pesa PIN to complete payment</p>
                  </div>
                  <button
                    onClick={checkPaymentStatus}
                    disabled={checkingPayment}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {checkingPayment ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Checking...
                      </>
                    ) : (
                      'I\'ve completed the payment'
                    )}
                  </button>
                  <button
                    onClick={() => setPaymentSent(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Try a different method
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOrders;
